import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Utility: Fetch and set profile for uid
  const fetchProfile = useCallback(async (uid: string) => {
    try {
      const { data: prof, error: e } = await supabase
        .from('profiles')
        .select('*').eq('id', uid)
        .single();
      if (e) {
        setProfile(null);
        setError("Failed to fetch user profile.");
      } else {
        setProfile(prof);
        setError(null);
      }
    } catch (err) {
      setProfile(null);
      setError("Error loading profile.");
    }
  }, []);

  useEffect(() => {
    setLoading(true);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Shorter timeout for initial auth check (3 seconds)
    timeoutRef.current = setTimeout(() => {
      console.warn('Auth loading timeout - proceeding without profile data');
      setLoading(false);
      // Don't set error for timeout, just proceed
    }, 3000); // Reduced from 8s to 3s

    const getInitialSession = async () => {
      try {
        // Get session first (fast)
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          setUser(session.user);
          setSession(session);
          
          // Clear timeout since we have user
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            setLoading(false); // Set loading false immediately after getting user
          }
          
          // Fetch profile separately (non-blocking)
          fetchProfile(session.user.id).catch(err => {
            console.warn('Profile fetch failed, continuing without profile:', err);
          });
        } else {
          setUser(null);
          setSession(null);
          setProfile(null);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setLoading(false);
        }
      } catch (err) {
        console.warn("Session check failed:", err);
        setUser(null);
        setSession(null);
        setProfile(null);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setLoading(false);
      }
    };
    
    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (session?.user) {
          setUser(session.user);
          setSession(session);
          // Profile fetch is non-blocking
          fetchProfile(session.user.id).catch(err => {
            console.warn('Profile fetch failed during auth change:', err);
          });
        } else {
          setUser(null);
          setSession(null);
          setProfile(null);
        }
      }
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signOut = async () => {
    setLoading(true);
    try {
      const { error: e } = await supabase.auth.signOut();
      if (e) throw e;
    } catch (err: any) {
      setError("Error signing out.");
    } finally {
      setUser(null);
      setSession(null);
      setProfile(null);
      setLoading(false);
    }
  };

  // Allow consumers to force fresh profile reload (after update)
  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [fetchProfile, user]);

  const value = {
    user,
    session,
    profile,
    loading,
    error,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
